'use client'

import { type RefObject, useCallback, useMemo } from 'react'

import { instrumentTabType } from '@/features/dashboard/lib/instrument-tab-type'
import {
  resolveBaseCurrency,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { useCanTradeExchange } from '@/hooks/use-can-trade-exchange'
import { useCardAccountContext } from '@/hooks/use-card-account'
import { useLinkingGroupWatchList } from '@/hooks/use-linking-group-state'
import { useOrderFormDispatch } from '@/hooks/use-order-form-dispatch'
import { useOrderFormPopup } from '@/hooks/use-order-form-popup'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { isOrderEligibleAccount } from '@/lib/utils/account-select'
import { useUserAccounts } from '@/service/account.hook'
import { OrderFormSummaryTab } from '@/state/order'
import {
  type AccountBinding,
  INHERITED_ACCOUNT_BINDING,
  type SelectedInstrument,
  type SelectedOption,
} from '@/types/dashboard'
import {
  type Exchange,
  type Instrument,
  InstrumentKind,
  OptionType,
} from '@/types/generated/common/types'

/** Where the Order Form popup anchors: the click point, else the chart widget's own box. */
export type ChartOrderFormAnchor = {
  clientX?: number
  clientY?: number
}

function normalizeInstrumentKind(kind?: string) {
  const normalized = kind?.toLowerCase()
  if (normalized === 'perps' || normalized === 'perp' || normalized === 'future' || kind === '2') {
    return 'derivative'
  }
  return normalized ?? ''
}

/**
 * The account a row added from a chart goes on: the one the chart's own account selector
 * shows, always — not the Order Form's per-venue pick. Must be able to trade this symbol: a
 * viewing-only (read-only/inactive) or other-venue account falls back to the card's trading
 * account, and with neither the row stays inherited (the form's own resolution).
 */
export function useChartOriginOrderAccountId(cardId: string, exchange: Exchange) {
  const { accountId: chartAccountId, tradingAccountId: chartTradingAccountId } =
    useCardAccountContext(cardId)
  const { data: userAccountsData } = useUserAccounts({ showDisabled: true })
  return useMemo(() => {
    const userAccounts = userAccountsData?.accounts ?? []
    const symbolVenue = exchangeToTradeVenue(exchange)
    const tradable = (accountId: string | undefined) => {
      const account = accountId
        ? userAccounts.find((candidate) => candidate.accountId === accountId)
        : undefined
      return account && isOrderEligibleAccount(account) && account.tradeVenue === symbolVenue
        ? account.accountId
        : undefined
    }
    return tradable(chartAccountId) ?? tradable(chartTradingAccountId)
  }, [chartAccountId, chartTradingAccountId, exchange, userAccountsData])
}

interface UseChartOrderFormAppendOptions {
  cardId: string
  symbol: string
  exchange: Exchange
  /** This chart's instrument, from its own exchange's catalog (not the active account's). */
  symbolInstrument: Instrument | null
  /** Shown on the added row until the form's own quote takes over. */
  lastPrice: string | undefined
  /** The chart element; the popup anchors to its widget when there's no click point. */
  anchorRef: RefObject<HTMLElement | null>
}

/**
 * Adds this chart's symbol to the linked Order Form as a Limit order at `price` and opens the
 * form — what both chart engines' Buy/Sell buttons, price-scale "+" and context-menu actions do.
 * An option goes to the form's options list; anything else to its instruments, merged into the
 * existing row for the same symbol and account.
 */
export function useChartOrderFormAppend({
  cardId,
  symbol,
  exchange,
  symbolInstrument,
  lastPrice,
  anchorRef,
}: UseChartOrderFormAppendOptions) {
  const watchList = useLinkingGroupWatchList(cardId)
  const canTrade = useCanTradeExchange()
  const { setSelectedInstruments, setSelectedOptions, setOrderFormTab, setOrderbookPrice } =
    useOrderFormDispatch(cardId)
  const { openOrderFormPopup } = useOrderFormPopup()
  const originOrderAccountId = useChartOriginOrderAccountId(cardId, exchange)
  const symbolTickSize = symbolInstrument?.tickSize || undefined

  return useCallback(
    (price: number, side: 'buy' | 'sell', params?: ChartOrderFormAnchor) => {
      if (!Number.isFinite(price) || price <= 0) return
      if (!canTrade(exchange)) return

      // A row is identified by (account, symbol): the same symbol on another account is a
      // separate row — only a row on the SAME account is merged into. Without an origin
      // account the row is inherited and may only merge into another inherited row; a row
      // already bound to an account (position, another chart account) is never retargeted.
      const originBinding: AccountBinding = originOrderAccountId
        ? { kind: 'scoped', reason: 'origin-selection' }
        : INHERITED_ACCOUNT_BINDING
      const isSameOriginAccount = (
        rowAccountId: string | undefined,
        rowBinding: AccountBinding | undefined
      ) =>
        originOrderAccountId ? rowAccountId === originOrderAccountId : rowBinding?.kind !== 'scoped'

      const selectedWatchListItem = watchList?.list[watchList?.selectedIndex]
      const watchListItem =
        selectedWatchListItem?.symbol === symbol
          ? selectedWatchListItem
          : watchList?.list.find((item) => item.symbol === symbol)

      if (symbolInstrument?.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) {
        const chartOption: SelectedOption = {
          // Added on the chart's own selected account (see useChartOriginOrderAccountId).
          accountId: originOrderAccountId,
          accountBinding: originBinding,
          exchange: exchange.toString(),
          instrumentName: symbol,
          side: side === 'buy' ? 'ask' : 'bid',
          tradeSide: side,
          price: price.toString(),
          strikePrice: symbolInstrument.strikePrice,
          optionType: symbolInstrument.optionType === OptionType.OPTION_TYPE_CALL ? 'call' : 'put',
        }
        setSelectedOptions((prev) => {
          const existingIndex = prev.findIndex(
            (option) =>
              option.instrumentName === symbol &&
              isSameOriginAccount(option.accountId, option.accountBinding)
          )
          if (existingIndex < 0) return [...prev, chartOption]

          return prev.map((option, index) =>
            index === existingIndex
              ? {
                  ...option,
                  ...chartOption,
                }
              : option
          )
        })
      } else {
        const catalogInstrumentKind = symbolInstrument
          ? instrumentTabType(symbolInstrument)
          : undefined
        const chartInstrument: SelectedInstrument = {
          accountId: originOrderAccountId,
          accountBinding: originBinding,
          symbol,
          baseCurrency: resolveBaseCurrency([], symbol, symbolInstrument),
          quoteCurrency: resolveQuoteCurrency([], symbol, symbolInstrument),
          // Venue symbols are identifiers, not product metadata: Bybit's USDC
          // perpetual is `BTCPERP`, while its spot is `BTCUSDC`. Trust the catalog
          // before a possibly stale watch-list type or symbol-shape fallback.
          instrumentKind:
            catalogInstrumentKind ??
            watchListItem?.type ??
            (symbol.toLowerCase().includes('-perp') ? 'perps' : 'spot'),
          side,
          lastPrice,
          exchange: exchange.toString(),
          exchangeSymbol: symbolInstrument?.exchangeSymbol,
          exchangeId: symbolInstrument?.exchangeId,
          internalId: symbolInstrument?.internalId,
          settlementCurrency: symbolInstrument?.settlementCurrency,
          tickSize: symbolInstrument?.tickSize,
          minTradeAmount: symbolInstrument?.minTradeAmount,
          contractSize: symbolInstrument?.contractSize,
          pricePrecision: symbolTickSize ?? watchListItem?.pricePrecision,
          qtyPrecision: symbolInstrument?.qtyPrecision,
          expirationTsMs: symbolInstrument?.expirationTsMs,
          fundingInterval: symbolInstrument?.fundingInterval,
          type: symbolInstrument?.type,
        }

        setSelectedInstruments((prev) => {
          const chartInstrumentKind = normalizeInstrumentKind(chartInstrument.instrumentKind)
          const existingIndex = prev.findIndex(
            (item) =>
              item.symbol === symbol &&
              normalizeInstrumentKind(item.instrumentKind) === chartInstrumentKind &&
              isSameOriginAccount(item.accountId, item.accountBinding)
          )
          if (existingIndex < 0) return [...prev, chartInstrument]

          return prev.map((item, index) =>
            index === existingIndex
              ? {
                  ...item,
                  ...chartInstrument,
                  side,
                }
              : item
          )
        })
      }
      // A price-scale action is an explicit limit-order intent. Route this to
      // the same live Order Form target as the selection and price so it also
      // overrides a form that the user previously left on Market.
      setOrderFormTab(OrderFormSummaryTab.Limit)
      setOrderbookPrice(price.toString())

      const widgetEl = anchorRef.current?.closest('.react-grid-item') as HTMLElement | null
      const rect = widgetEl?.getBoundingClientRect() ?? anchorRef.current?.getBoundingClientRect()
      const anchor =
        typeof params?.clientX === 'number' && typeof params?.clientY === 'number'
          ? {
              left: params.clientX,
              right: params.clientX,
              top: params.clientY,
              bottom: params.clientY,
            }
          : rect
            ? { right: rect.right, left: rect.left, top: rect.top, bottom: rect.bottom }
            : null

      openOrderFormPopup(cardId, anchor)
    },
    [
      anchorRef,
      canTrade,
      cardId,
      exchange,
      lastPrice,
      openOrderFormPopup,
      originOrderAccountId,
      setOrderFormTab,
      setOrderbookPrice,
      setSelectedInstruments,
      setSelectedOptions,
      symbol,
      symbolInstrument,
      symbolTickSize,
      watchList?.list,
      watchList?.selectedIndex,
    ]
  )
}
